import React, { useState } from 'react'
import { Bookmark } from 'lucide-react'
import { ToggleButton } from '@astryxdesign/core/ToggleButton'
import AuthModal from './AuthModal'

interface SaveStoryButtonProps {
  publicationId: string
  isSaved: boolean
  saveCount: number
  isSignedIn: boolean
  onSavedChange: (next: { saved: boolean; saveCount: number }) => void
  size?: 'sm' | 'md' | 'lg'
}

/**
 * The bookmark toggle on a published story.
 *
 * The count shown is local state seeded from props so the number moves on click rather than after
 * the round trip; the server's own count replaces it on success and the optimistic value is
 * discarded on failure. onSavedChange fires only after the server agrees, so a page that renders
 * this twice never gets two different numbers from one failed request.
 */
export default function SaveStoryButton({
  publicationId,
  isSaved,
  saveCount,
  isSignedIn,
  onSavedChange,
  size = 'md'
}: SaveStoryButtonProps): React.JSX.Element {
  const [saved, setSaved] = useState(isSaved)
  const [count, setCount] = useState(saveCount)
  const [showAuth, setShowAuth] = useState(false)

  const toggle = async (): Promise<void> => {
    if (!isSignedIn) {
      setShowAuth(true)
      return
    }

    const previous = { saved, count }
    setSaved(!saved)
    setCount(Math.max(0, saved ? count - 1 : count + 1))
    try {
      const result = saved
        ? await window.api.storySocial.unsave(publicationId)
        : await window.api.storySocial.save(publicationId)
      setSaved(result.saved)
      setCount(result.saveCount)
      onSavedChange(result)
    } catch {
      setSaved(previous.saved)
      setCount(previous.count)
    }
  }

  // One string for both the label and the visible text. ToggleButton uses `label` as the
  // accessible name, so letting the two diverge would announce "Save" to a screen reader while
  // the button reads "Saved · 12".
  const text = count > 0 ? `${saved ? 'Saved' : 'Save'} · ${count}` : saved ? 'Saved' : 'Save'

  return (
    <>
      <ToggleButton
        label={text}
        size={size}
        isPressed={saved}
        icon={<Bookmark size={16} />}
        pressedIcon={<Bookmark size={16} fill="currentColor" />}
        pressedChangeAction={() => toggle()}
      >
        {text}
      </ToggleButton>
      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} initialMode="signIn" />
    </>
  )
}
